import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserPlus, Mail, Lock, User, Loader, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

import { apiService } from '../services/api';
import { applyBrandingColors } from '../utils/branding';

function Register() {
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [invitation, setInvitation] = useState(null);
    const [invitationLoading, setInvitationLoading] = useState(true);
    const [invitationError, setInvitationError] = useState(null);

    const [formData, setFormData] = useState({
        email: '',
        fullName: '',
        password: '',
        confirmPassword: '',
        phone: ''
    });
    const [loading, setLoading] = useState(false);
    const [branding, setBranding] = useState({
        companyName: 'Sensity',
        companyLogo: null,
        primaryColor: '#2563eb'
    });

    // Load branding settings
    useEffect(() => {
        const loadBranding = async () => {
            try {
                // Try localStorage first
                const savedSettings = localStorage.getItem('appSettings');
                if (savedSettings) {
                    try {
                        const parsed = JSON.parse(savedSettings);
                        if (parsed.branding) {
                            setBranding(prev => ({ ...prev, ...parsed.branding }));
                        }
                    } catch (error) {
                        console.error('Error loading branding from localStorage:', error);
                    }
                }

                // Try to load from API (public branding endpoint)
                const response = await apiService.getBrandingSettings();
                if (response?.branding) {
                    setBranding(prev => ({ ...prev, ...response.branding }));
                }
            } catch (error) {
                // Settings API might not be available during registration
                console.warn('Could not load branding settings:', error);
            }
        };

        loadBranding();
    }, []);

    // Apply branding colors
    useEffect(() => {
        if (branding.primaryColor) {
            applyBrandingColors(branding.primaryColor);
        }
    }, [branding.primaryColor]);

    useEffect(() => {
        if (token) {
            verifyInvitation();
        } else {
            setInvitationError('No invitation token provided');
            setInvitationLoading(false);
        }
    }, [token]);

    const verifyInvitation = async () => {
        try {
            const invitationData = await apiService.verifyInvitation(token);
            setInvitation(invitationData);
            setFormData(prev => ({
                ...prev,
                email: invitationData.email,
                fullName: invitationData.fullName
            }));
        } catch (error) {
            const message = error.response?.data?.error || 'Invalid invitation token';
            setInvitationError(message);
        } finally {
            setInvitationLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.password !== formData.confirmPassword) {
            toast.error(t('auth.passwordMismatch', 'Passwords do not match'));
            return;
        }

        if (formData.password.length < 6) {
            toast.error(t('auth.passwordTooShort', 'Password must be at least 6 characters'));
            return;
        }

        setLoading(true);

        try {
            const { confirmPassword, ...payload } = formData;
            await apiService.register({
                ...payload,
                inviteToken: token,
                preferredLanguage: i18n.language
            });

            toast.success(t('auth.registrationSuccess', 'Account created successfully! You can now log in.'));
            navigate('/login');
        } catch (error) {
            const message = error.response?.data?.error || t('auth.registrationError', 'Registration failed');
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    if (invitationLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="text-center">
                    <Loader className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
                    <p className="mt-4 text-gray-600">
                        {t('auth.verifyingInvitation', 'Verifying invitation...')}
                    </p>
                </div>
            </div>
        );
    }

    if (invitationError) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="max-w-md w-full">
                    <div className="bg-white rounded-2xl shadow-xl p-8 text-center space-y-6">
                        {/* Logo */}
                        {branding.companyLogo && (
                            <div className="mx-auto mb-4">
                                <img
                                    src={`${branding.companyLogo}?${Date.now()}`}
                                    alt="Company Logo"
                                    className="h-16 w-auto mx-auto object-contain"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                        )}

                        <div
                            className="mx-auto h-12 w-12 flex items-center justify-center rounded-full"
                            style={{
                                background: `linear-gradient(135deg, #ef444420, #ef444440)`
                            }}
                        >
                            <XCircle className="h-6 w-6 text-red-600" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900">
                            {t('auth.invitationInvalid', 'Invalid Invitation')}
                        </h2>
                        <p className="text-sm text-gray-600">
                            {invitationError}
                        </p>
                        <Link
                            to="/login"
                            className="btn-primary inline-flex items-center"
                        >
                            {t('auth.backToLogin', 'Back to Login')}
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="max-w-md w-full">
                <div className="bg-white rounded-2xl shadow-xl p-8 space-y-8">
                    <div className="text-center">
                        {/* Logo */}
                        {branding.companyLogo && (
                            <div className="mx-auto mb-4">
                                <img
                                    src={`${branding.companyLogo}?${Date.now()}`}
                                    alt="Company Logo"
                                    className="h-16 w-auto mx-auto object-contain"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                    }}
                                />
                            </div>
                        )}

                        {/* Fallback icon if no logo */}
                        {!branding.companyLogo && (
                            <div
                                className="mx-auto h-16 w-16 mb-4 flex items-center justify-center rounded-full"
                                style={{
                                    background: `linear-gradient(135deg, #10b98120, #10b98140)`
                                }}
                            >
                                <CheckCircle className="h-8 w-8 text-green-600" />
                            </div>
                        )}

                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                            {t('auth.createAccount', 'Create your account')}
                        </h2>
                        <div>
                            <p className="text-sm text-gray-600">
                                {t('auth.invitedAs', 'You have been invited as')}{' '}
                                <span className="font-medium" style={{ color: branding.primaryColor }}>
                                    {t(`roles.${invitation?.role}`, invitation?.role)}
                                </span>
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                                {t('auth.expiresAt', 'Expires')}: {' '}
                                {invitation?.expiresAt ?
                                    new Date(invitation.expiresAt).toLocaleDateString() :
                                    'N/A'
                                }
                            </p>
                        </div>
                    </div>

                    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                    {t('auth.email', 'Email address')}
                                </label>
                                <div className="relative mt-1">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Mail className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        required
                                        disabled
                                        className="block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed sm:text-sm"
                                        value={formData.email}
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                                    {t('auth.fullName', 'Full Name')}
                                </label>
                                <div className="relative mt-1">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <User className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        id="fullName"
                                        name="fullName"
                                        type="text"
                                        required
                                        className="input-field pl-10"
                                        value={formData.fullName}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                    {t('auth.phone', 'Phone Number')} ({t('common.optional', 'Optional')})
                                </label>
                                <input
                                    id="phone"
                                    name="phone"
                                    type="tel"
                                    className="input-field mt-1"
                                    value={formData.phone}
                                    onChange={handleChange}
                                />
                            </div>

                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                    {t('auth.password', 'Password')}
                                </label>
                                <div className="relative mt-1">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Lock className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        required
                                        className="input-field pl-10"
                                        placeholder={t('auth.passwordPlaceholder', 'Enter your password')}
                                        value={formData.password}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                    {t('auth.confirmPassword', 'Confirm Password')}
                                </label>
                                <div className="relative mt-1">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Lock className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        type="password"
                                        required
                                        className="input-field pl-10"
                                        placeholder={t('auth.confirmPasswordPlaceholder', 'Confirm your password')}
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-primary w-full flex justify-center py-3 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading && <Loader className="h-4 w-4 mr-2 animate-spin" />}
                                {t('auth.createAccount', 'Create Account')}
                            </button>
                        </div>

                        <div className="text-center">
                            <p className="text-sm text-gray-600">
                                {t('auth.alreadyHaveAccount', 'Already have an account?')}{' '}
                                <Link
                                    to="/login"
                                    className="font-medium hover:underline"
                                    style={{ color: branding.primaryColor }}
                                >
                                    {t('auth.signIn', 'Sign in')}
                                </Link>
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default Register;
